import { newrelic } from "@jti/fe-clients";
import { type Address } from "@jti/fe-common";
import { useGetCustomerCartIdQuery, useSetShippingAddressOnCartMutation } from "@jti/store/slices/magentoApiSlice";
import { useTranslations } from "@jti/ui";

import { profileAddressToMagentoAddressAdapter } from "@checkout-v2/forms/ShippingForm/utils/profileAddressToMagentoAddressAdapter";
import { useCountry } from "@checkout-v2/shared/hooks/useCountry";
import { sanitizeAddressRegionData } from "@checkout-v2/shared/utils/sanitizeAddressRegionData";

export const SET_SHIPPING_ADDRESS_ON_CART_MUTATION_FIXED_CACHE_KEY = "shared-set-shipping-address-on-cart-mutation";

export const useSetShippingAddressOnCart = () => {
  const { __ } = useTranslations();
  const getCustomerCartIdQuery = useGetCustomerCartIdQuery();
  const countryQueryState = useCountry();

  const customerCartId = getCustomerCartIdQuery.data?.customerCart.id;

  const [setShippingAddressOnCartMutation, setShippingAddressOnCartMutationState] = useSetShippingAddressOnCartMutation(
    {
      fixedCacheKey: SET_SHIPPING_ADDRESS_ON_CART_MUTATION_FIXED_CACHE_KEY,
    }
  );

  const setShippingAddressOnCart = async (address: Address) => {
    if (!customerCartId) {
      const error = new Error(__("cart.checkout.shipping_form.error.shipping_address.missing_cart_id"));

      newrelic.noticeError(error);
      throw error;
    }

    const sanitizedAddress = sanitizeAddressRegionData(address, countryQueryState.data?.availableRegions);

    return await setShippingAddressOnCartMutation({
      cartId: customerCartId,
      address: profileAddressToMagentoAddressAdapter(sanitizedAddress),
    }).unwrap();
  };

  return {
    setShippingAddressOnCart,
    setShippingAddressOnCartState: setShippingAddressOnCartMutationState,
  };
};
